import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { renderRich } from "@/components/note/shared";
import { toast } from "sonner";
import { Sparkles, RefreshCw, ChevronLeft, ChevronRight, RotateCw, Check, X as XIcon, Trophy, HelpCircle, Layers as LayersIcon, Target, TrendingDown } from "lucide-react";

/**
 * Study tab: interactive MCQ quiz + swipeable flashcards + weak-topic radar.
 * Adaptive: server decides target count (10 / 15 / 20) based on note density.
 */
export default function StudyPanel({ noteId }) {
  const [tab, setTab] = useState("mcqs");
  return (
    <div data-testid="study-panel">
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          data-testid="study-tab-mcqs"
          onClick={() => setTab("mcqs")}
          className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 border-2 border-black transition-colors ${
            tab === "mcqs" ? "bg-black text-white" : "hover:bg-black hover:text-white"
          }`}
        >
          <HelpCircle className="w-3.5 h-3.5" /> MCQs
        </button>
        <button
          data-testid="study-tab-flashcards"
          onClick={() => setTab("flashcards")}
          className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 border-2 border-black transition-colors ${
            tab === "flashcards" ? "bg-black text-white" : "hover:bg-black hover:text-white"
          }`}
        >
          <LayersIcon className="w-3.5 h-3.5" /> Flashcards
        </button>
        <button
          data-testid="study-tab-weak"
          onClick={() => setTab("weak")}
          className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 border-2 border-black transition-colors ${
            tab === "weak" ? "bg-black text-white" : "hover:bg-black hover:text-white"
          }`}
        >
          <Target className="w-3.5 h-3.5" /> Weak topics
        </button>
      </div>

      {tab === "mcqs" && <MCQQuiz noteId={noteId} />}
      {tab === "flashcards" && <Flashcards noteId={noteId} />}
      {tab === "weak" && <WeakTopics noteId={noteId} />}
    </div>
  );
}

/* ─────────────────────────  MCQs  ───────────────────────── */
function MCQQuiz({ noteId }) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [payload, setPayload] = useState(null);
  const [target, setTarget] = useState(10);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/notes/${noteId}/study/mcqs`);
        if (!alive) return;
        setTarget(data.target || 10);
        setPayload(data.payload || null);
      } catch (e) {
        if (alive) toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [noteId]);

  const generate = async () => {
    setGenerating(true);
    setSubmitted(false);
    setAnswers({});
    try {
      const { data } = await api.post(`/notes/${noteId}/study/mcqs`);
      setPayload(data);
      toast.success(`Generated ${data.count} question${data.count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const score = useMemo(() => {
    if (!submitted || !payload) return 0;
    return (payload.questions || []).reduce(
      (acc, q, i) => acc + (answers[i] === q.correct_index ? 1 : 0),
      0,
    );
  }, [submitted, payload, answers]);

  const submitQuiz = async () => {
    setSubmitted(true);
    const questions = payload?.questions || [];
    const attempts = questions.map((q, i) => ({
      note_id: noteId,
      question_index: i,
      concept_tag: q.concept_tag || "General",
      correct: answers[i] === q.correct_index,
    }));
    try {
      await api.post("/study/attempts", { attempts });
    } catch {
      // silent — not blocking user
    }
  };

  if (loading) {
    return <div data-testid="mcqs-loading" className="font-mono text-sm text-zinc-500">Loading…</div>;
  }

  if (!payload) {
    return (
      <div data-testid="mcqs-empty" className="border-2 border-dashed border-black p-8 text-center bg-[#FEF9C3]">
        <p className="font-heading font-bold text-2xl">Test yourself</p>
        <p className="mt-2 text-sm text-zinc-700">
          Turn this note into <b>{target}</b> multiple-choice questions. Instant scoring, 1-line explanations.
        </p>
        <button
          data-testid="mcqs-generate-btn"
          onClick={generate}
          disabled={generating}
          className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-5 py-3 bg-[#3B82F6] text-white btn-brutal disabled:opacity-60"
        >
          <Sparkles className="w-4 h-4" />
          {generating ? "Generating…" : `Generate ${target} MCQs`}
        </button>
      </div>
    );
  }

  const questions = payload.questions || [];
  const total = questions.length;
  const answeredCount = Object.keys(answers).length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-black text-white">
            {total} questions
          </span>
          {submitted && (
            <span
              data-testid="mcqs-score"
              className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest px-3 py-1 bg-[#DBEAFE] border-2 border-black"
            >
              <Trophy className="w-3.5 h-3.5" /> {score} / {total}
            </span>
          )}
          {!submitted && (
            <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
              {answeredCount} / {total} answered
            </span>
          )}
        </div>
        <button
          data-testid="mcqs-regenerate-btn"
          onClick={generate}
          disabled={generating}
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest px-3 py-2 border-2 border-black hover:bg-black hover:text-white transition-colors disabled:opacity-50"
          title="Generate a fresh set"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${generating ? "animate-spin" : ""}`} /> Regenerate
        </button>
      </div>

      <ol className="space-y-6">
        {questions.map((q, i) => (
          <MCQCard
            key={i}
            index={i}
            question={q}
            picked={answers[i]}
            submitted={submitted}
            onPick={(idx) => setAnswers((a) => ({ ...a, [i]: idx }))}
          />
        ))}
      </ol>

      <div className="mt-8 flex flex-wrap gap-3 items-center">
        {!submitted ? (
          <button
            data-testid="mcqs-submit-btn"
            onClick={submitQuiz}
            disabled={answeredCount < total}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-5 py-3 bg-[#3B82F6] text-white btn-brutal disabled:opacity-40"
          >
            Submit answers
          </button>
        ) : (
          <button
            data-testid="mcqs-retry-btn"
            onClick={() => { setSubmitted(false); setAnswers({}); }}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-5 py-3 border-2 border-black hover:bg-black hover:text-white transition-colors"
          >
            <RotateCw className="w-4 h-4" /> Retry
          </button>
        )}
        {answeredCount < total && !submitted && (
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            Answer all questions to submit
          </span>
        )}
      </div>
    </div>
  );
}

function MCQCard({ index, question, picked, submitted, onPick }) {
  const letters = ["A", "B", "C", "D"];
  return (
    <li
      data-testid={`mcq-item-${index}`}
      className="border-2 border-black bg-white p-5 shadow-[4px_4px_0_0_#0A0A0A]"
    >
      <p className="font-heading font-bold text-lg mb-4">
        <span className="font-mono text-sm text-[#3B82F6] mr-2">Q{index + 1}.</span>
        {renderRich(question.question)}
      </p>
      <div className="space-y-2">
        {question.options.map((opt, j) => {
          const isPicked = picked === j;
          const isCorrect = submitted && j === question.correct_index;
          const isWrongPick = submitted && isPicked && j !== question.correct_index;
          return (
            <button
              key={j}
              data-testid={`mcq-${index}-option-${j}`}
              disabled={submitted}
              onClick={() => onPick(j)}
              className={`w-full text-left px-4 py-3 border-2 flex items-center gap-3 font-mono text-sm transition-colors ${
                isCorrect
                  ? "border-[#22C55E] bg-[#DCFCE7]"
                  : isWrongPick
                  ? "border-[#FF3333] bg-[#FEE2E2]"
                  : isPicked
                  ? "border-black bg-[#DBEAFE]"
                  : "border-black hover:bg-[#F0F9FF]"
              } ${submitted ? "cursor-default" : "cursor-pointer"}`}
            >
              <span className="inline-flex items-center justify-center w-6 h-6 bg-black text-white text-xs font-bold shrink-0">
                {letters[j]}
              </span>
              <span className="flex-1">{renderRich(opt)}</span>
              {isCorrect && <Check className="w-4 h-4 text-[#166534]" />}
              {isWrongPick && <XIcon className="w-4 h-4 text-[#991B1B]" />}
            </button>
          );
        })}
      </div>
      {submitted && question.explanation && (
        <p
          data-testid={`mcq-${index}-explanation`}
          className="mt-4 text-sm bg-[#FEF9C3] border-l-4 border-[#EAB308] px-3 py-2"
        >
          <b className="font-mono text-[10px] uppercase tracking-widest text-[#854D0E] mr-2">Why:</b>
          {renderRich(question.explanation)}
        </p>
      )}
    </li>
  );
}

/* ─────────────────────────  Flashcards  ───────────────────────── */
function Flashcards({ noteId }) {
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [status, setStatus] = useState({}); // { i: "got" | "review" }

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/notes/${noteId}/study/flashcards`);
        if (!alive) return;
        setCards(data.cards || []);
      } catch (e) {
        if (alive) toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [noteId]);

  if (loading) {
    return <div data-testid="fc-loading" className="font-mono text-sm text-zinc-500">Loading…</div>;
  }
  if (!cards.length) {
    return (
      <div data-testid="fc-empty" className="border-2 border-dashed border-black p-8 text-center bg-[#F0F9FF]">
        <p className="font-heading font-bold text-2xl">No flashcards yet</p>
        <p className="mt-2 text-sm text-zinc-700">
          Flashcards are built from Definitions, Formulas, Key Concepts, and Exam Questions in your note.
        </p>
      </div>
    );
  }

  const card = cards[idx];
  const total = cards.length;
  const gotCount = Object.values(status).filter((s) => s === "got").length;
  const reviewCount = Object.values(status).filter((s) => s === "review").length;

  const next = () => {
    setFlipped(false);
    setIdx((i) => Math.min(i + 1, total - 1));
  };
  const prev = () => {
    setFlipped(false);
    setIdx((i) => Math.max(i - 1, 0));
  };
  const mark = (kind) => {
    setStatus((s) => ({ ...s, [idx]: kind }));
    if (idx < total - 1) next();
  };

  const kindLabels = {
    definition: "Definition",
    formula: "Formula",
    concept: "Concept",
    exam: "Exam question",
  };

  return (
    <div data-testid="flashcards-viewer">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-black text-white">
          {idx + 1} / {total}
        </span>
        <div className="flex gap-2">
          <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-[#DCFCE7] border border-black">
            ✓ {gotCount}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-[#FEE2E2] border border-black">
            ↺ {reviewCount}
          </span>
        </div>
      </div>

      <button
        data-testid="flashcard-flip"
        onClick={() => setFlipped((f) => !f)}
        className="w-full min-h-[280px] border-2 border-black bg-white p-8 shadow-[6px_6px_0_0_#0A0A0A] text-left flex flex-col hover:bg-[#F0F9FF] transition-colors"
      >
        <div className="flex items-center justify-between mb-4">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#3B82F6]">
            / {kindLabels[card.kind] || "Card"}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            {flipped ? "Answer" : "Tap to flip"}
          </span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            {flipped ? (
              <p data-testid="flashcard-back" className="font-heading text-xl leading-relaxed">
                {renderRich(card.back)}
                {card.units && (
                  <span className="block mt-2 font-mono text-xs text-zinc-500">Units: {card.units}</span>
                )}
              </p>
            ) : (
              <p data-testid="flashcard-front" className="font-heading font-bold text-2xl leading-tight">
                {renderRich(card.front)}
              </p>
            )}
          </div>
        </div>
      </button>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <button
            data-testid="flashcard-prev"
            onClick={prev}
            disabled={idx === 0}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-3 py-2 border-2 border-black hover:bg-black hover:text-white transition-colors disabled:opacity-40"
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <button
            data-testid="flashcard-next"
            onClick={next}
            disabled={idx >= total - 1}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-3 py-2 border-2 border-black hover:bg-black hover:text-white transition-colors disabled:opacity-40"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="flashcard-review"
            onClick={() => mark("review")}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-3 py-2 border-2 border-[#FF3333] text-[#FF3333] hover:bg-[#FF3333] hover:text-white transition-colors"
          >
            <RotateCw className="w-3.5 h-3.5" /> Review again
          </button>
          <button
            data-testid="flashcard-got"
            onClick={() => mark("got")}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-3 py-2 bg-[#22C55E] text-white btn-brutal"
          >
            <Check className="w-3.5 h-3.5" /> Got it
          </button>
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────────  Weak topics (per note)  ───────────────────────── */
function WeakTopics({ noteId }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/notes/${noteId}/study/weak-topics`);
        if (!alive) return;
        setRows(data.weak_topics || []);
      } catch (e) {
        if (alive) toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [noteId]);

  if (loading) {
    return <div data-testid="weak-loading" className="font-mono text-sm text-zinc-500">Loading…</div>;
  }

  if (!rows.length) {
    return (
      <div data-testid="weak-empty" className="border-2 border-dashed border-black p-8 text-center bg-[#F0F9FF]">
        <Target className="w-8 h-8 mx-auto mb-3 text-[#3B82F6]" />
        <p className="font-heading font-bold text-2xl">No weak topics yet</p>
        <p className="mt-2 text-sm text-zinc-700">
          Take the MCQ quiz above. Concepts you keep missing will show up here so you can revisit them fast.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="weak-topics-list">
      <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-4">
        Concepts on this note where your accuracy is below the mark. Rebuild these first.
      </p>
      <ul className="space-y-3">
        {rows.map((r, i) => (
          <li
            key={r.concept_tag + i}
            data-testid={`weak-topic-${i}`}
            className="border-2 border-black bg-white p-4 shadow-[3px_3px_0_0_#0A0A0A]"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-[#FF3333]" />
                <span className="font-heading font-bold text-lg">{r.concept_tag}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[#FEE2E2] text-[#991B1B] border border-black">
                  {Math.round(r.accuracy * 100)}%
                </span>
                {r.confidence === "soft" && (
                  <span
                    className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[#FEF9C3] text-[#854D0E] border border-black"
                    title="Fewer than 10 attempts — signal is soft"
                  >
                    early
                  </span>
                )}
              </div>
              <span className="font-mono text-[11px] text-zinc-500">
                {r.correct} / {r.attempts} correct
              </span>
            </div>
            <div className="mt-3 h-2 w-full bg-[#F0F9FF] border border-black">
              <div
                className="h-full bg-[#FF3333]"
                style={{ width: `${Math.round((1 - r.accuracy) * 100)}%` }}
                aria-hidden
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
